import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const VideoEmbed = ({ video, studentName, studentEmail }) => {
  if (!video.embedUrl) return null;

  if (video.type === 'youtube' || video.type === 'gdrive') {
    return (
      <div className="relative rounded-lg overflow-hidden bg-black">
        {/* Video iframe */}
        <div className="relative" style={{ paddingBottom: '56.25%' }}>
          <iframe
            src={video.embedUrl}
            title={video.title || 'Video'}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        {/* Top overlay — blocks YouTube Share/title bar clicks */}
        <div className="absolute top-0 left-0 right-0 h-12 z-10" />
        {/* Watermark overlay */}
        <div className="absolute top-2 right-2 z-20 bg-black/30 backdrop-blur-sm text-white/60 text-xs px-2 py-1 rounded select-none pointer-events-none">
          {studentName || studentEmail}
        </div>
      </div>
    );
  }

  // Fallback: generic link (no embed possible)
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50">
      <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
        <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
        </svg>
      </div>
      <p className="text-sm font-medium text-gray-700">{video.title || 'Resource'}</p>
    </div>
  );
};

const MaterialsHub = () => {
  const { user } = useAuth();
  const [coursesMaterials, setCoursesMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVideos, setExpandedVideos] = useState({}); // { "courseId-week": true }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await api.get('/enroll/my');
        const active = (data.enrollments || []).filter(e => e.status === 'active' && e.course);

        const materialsResults = await Promise.allSettled(
          active.map(e => api.get(`/materials/student/${e.course._id}`))
        );

        const results = [];
        active.forEach((e, i) => {
          if (materialsResults[i].status === 'fulfilled') {
            const mData = materialsResults[i].value.data;
            if (mData.materials?.length > 0) {
              results.push({
                courseId: e.course._id,
                courseName: e.course.name,
                danceStyle: e.course.danceStyle,
                materials: mData.materials,
                attendanceCount: mData.attendanceCount || 0,
                totalWeeks: mData.totalWeeks || mData.materials.length,
                materialsPaused: mData.materialsPaused || false
              });
            }
          }
        });

        setCoursesMaterials(results);
      } catch (err) {
        console.error('Failed to load materials');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const toggleVideo = (courseId, week) => {
    const key = `${courseId}-${week}`;
    setExpandedVideos(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) return <div className="text-gray-500">Loading materials...</div>;

  const totalUnlocked = coursesMaterials.reduce(
    (sum, c) => sum + c.materials.filter(m => m.unlocked).length, 0
  );
  const totalWeeks = coursesMaterials.reduce((sum, c) => sum + c.totalWeeks, 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Materials</h1>
        {totalWeeks > 0 && (
          <span className="text-sm text-gray-500">{totalUnlocked} of {totalWeeks} total weeks unlocked</span>
        )}
      </div>

      {coursesMaterials.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No materials yet</h3>
          <p className="text-gray-500 text-sm">Materials will appear here as your teacher adds them and you attend classes.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {coursesMaterials.map(course => {
            const unlockedCount = course.materials.filter(m => m.unlocked).length;
            const progressPercent = course.totalWeeks > 0 ? Math.round((unlockedCount / course.totalWeeks) * 100) : 0;

            return (
              <div key={course.courseId}>
                {/* Course header */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{course.courseName}</h2>
                    {course.danceStyle && (
                      <p className="text-sm text-gray-500">{course.danceStyle}</p>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">{unlockedCount}/{course.totalWeeks} unlocked</span>
                </div>

                {/* Materials paused banner */}
                {course.materialsPaused && (
                  <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
                    Materials access has been paused by your teacher. Please contact them for more information.
                  </div>
                )}

                {/* Progress bar */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-3">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>Progress</span>
                    <span className="font-medium">{progressPercent}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-brand-500 rounded-full h-2 transition-all"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>

                {/* Materials list */}
                <div className="space-y-2">
                  {course.materials.map(week => {
                    const videoKey = `${course.courseId}-${week.week}`;
                    const isExpanded = expandedVideos[videoKey];

                    return (
                      <div
                        key={week.week}
                        className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
                          week.unlocked ? 'border-gray-100' : 'border-gray-100 opacity-60'
                        }`}
                      >
                        <div className="p-4">
                          {/* Week header — clickable to expand/collapse videos */}
                          <button
                            className="w-full flex items-center gap-3"
                            onClick={() => week.unlocked && toggleVideo(course.courseId, week.week)}
                            disabled={!week.unlocked}
                          >
                            <span className={`px-2.5 py-1 rounded-lg text-sm font-bold ${
                              week.unlocked ? 'bg-brand-100 text-brand-600' : 'bg-gray-100 text-gray-400'
                            }`}>
                              Week {week.week}
                            </span>
                            {week.unlocked ? (
                              <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                              </svg>
                            )}
                            <span className={`text-sm flex-1 text-left ${week.unlocked ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                              {week.title || `Week ${week.week}`}
                            </span>
                            {week.unlocked && (
                              <span className="text-xs text-gray-400">
                                {week.videos?.length || 0} video{(week.videos?.length || 0) !== 1 ? 's' : ''}
                              </span>
                            )}
                            {week.unlocked && (
                              <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                              </svg>
                            )}
                          </button>

                          {/* Locked message */}
                          {!week.unlocked && (
                            <p className="text-xs text-gray-400 ml-4 mt-1">
                              This week's materials will be available soon
                            </p>
                          )}

                          {/* Expanded: embedded video players */}
                          {week.unlocked && isExpanded && (
                            <div className="space-y-4 mt-4 ml-4">
                              {week.videos?.map((video, vi) => (
                                <div key={vi}>
                                  <p className="text-sm font-medium text-gray-700 mb-2">
                                    {video.title || `Video ${vi + 1}`}
                                  </p>
                                  <VideoEmbed
                                    video={video}
                                    studentName={user?.name}
                                    studentEmail={user?.email}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MaterialsHub;
